import { Component, Input } from '@angular/core';
import { PEAK_BODY, PEAK_CAP, PEAK_CAP_SMALL } from '../line-peak-glyph';
import { ORB_BANDS, ORB_COLORS, ORB_R, ORB_RIM_OFF, ORB_RIM_STOPS, ORB_SETS, ORB_STARS } from '../line-orb-glyph';
import { LEAF_BLADE, LEAF_VEINS, TREE_DARK, TREE_LIGHT, TREE_MID } from '../line-tree-glyph';
import { MAPLE_BLADE, MAPLE_DEEP, MAPLE_GOLD, MAPLE_LIGHT, MAPLE_MID } from '../line-maple-glyph';
import { TEPUI_BODY, TEPUI_CROWN, TEPUI_DEEP, TEPUI_MID, TEPUI_OFF, TEPUI_RUNNELS, TEPUI_SEAM, TEPUI_SPIRE, TEPUI_SUN, TEPUI_WISP } from '../line-tepui-glyph';
import { TUMBLEWEED_HOOK, TUMBLEWEED_WRAP, TUMBLE_BODY, TUMBLE_OFF, TUMBLE_R, TUMBLE_STRAW } from '../line-tumbleweed-glyph';
import { SUN_COLORS, SUN_CY, SUN_HORIZON, SUN_HORIZON_D, SUN_OFF, SUN_R, SUN_RAY_LEN, sunRays } from '../line-sun-glyph';
import { MIAMI_PALM_CROWN, MIAMI_PALM_FIT, MIAMI_PALM_INK, MIAMI_PALM_TRUNK } from '../line-miami-palm-glyph';
import { GULL_PASSCODE, GULL_PC_OFF } from '../line-bird-glyph';
import { EIFFEL_BASE, EIFFEL_DECKS, EIFFEL_LEDGE_SHADOW, EIFFEL_SOLID } from '../line-eiffel-glyph';
import {
  PARASOL_PANELS_A, PARASOL_PANELS_B, PARASOL_PC_OFF, PARASOL_PIVOT, PARASOL_POLE,
  PARASOL_SOLID, PARASOL_GREEN, PARASOL_POSES, PARASOL_YELLOW,
} from '../line-parasol-glyph';
import { SNOWFLAKE, SNOW_PC_OFF, SNOW_PC_TILTS } from '../line-snowflake-glyph';
import { JELLYFISH, JELLY_ARMS, JELLY_BELL, JELLY_CREST, JELLY_PC_OFF, JELLY_TENTACLES } from '../line-jellyfish-glyph';
import { SHELL, SHELL_BODY, SHELL_EARS, SHELL_PC_OFF, SHELL_PC_TILTS, SHELL_RIBS } from '../line-shell-glyph';
import { LAMP, LAMP_GLASS, LAMP_IRON, LAMP_PC_OFF, LAMP_PC_TILTS } from '../line-lamp-glyph';
import { YACHT, YACHT_HULL, YACHT_JIB, YACHT_MAIN, YACHT_MAST , YACHT_PC_JIBS, YACHT_PC_OFF, YACHT_PC_TILTS } from '../line-yacht-glyph';
import { BASALT, BASALT_FACES, BASALT_FACES_ALT, BASALT_PC_OFF, BASALT_TOPS } from '../line-basalt-glyph';
import { FLOWER, FLOWER_FOLIAGE, FLOWER_GREEN, FLOWER_HEAD, FLOWER_SHADE, FLOWER_PC_OFF, FLOWER_PC_TILTS } from '../line-meadow-flower-glyph';
import { VIOLIN, VIOLIN_BRIDGE, VIOLIN_FHOLES, VIOLIN_PC_OFF, VIOLIN_PC_TILTS, VIOLIN_SHADE, VIOLIN_SHEEN, VIOLIN_STRINGS } from '../line-violin-glyph';
import { CLAVEL, CLAVEL_FOLIAGE, CLAVEL_GREEN, CLAVEL_HEAD, CLAVEL_INNER, CLAVEL_PC_OFF, CLAVEL_PC_TILTS } from '../line-clavel-glyph';
import { VOLCANO, VOLCANO_CONE, VOLCANO_FISSURE, VOLCANO_GLOW, VOLCANO_PC_OFF, VOLCANO_PC_TILTS, VOLCANO_PLUME, VOLCANO_RIVER, VOLCANO_SHOULDER } from '../line-volcano-glyph';
import { FUJI_MOUNT, FUJI_SNOW, PAGODA_PC_OFF, PAGODA_PC_TILTS } from '../line-pagoda-glyph';
import { mixHex } from '../line-electric-colors';

let _pcUid = 0;

/**
 * A single LINE passcode dot (iOS i_12..i_19 / Android a_12..a_19), drawn as a
 * live SVG instead of the Python generator. OFF = an open ring in the theme's
 * muted foreground; ON = a filled dot in one of the theme's colour circles
 * (compactPalette 2..5) with a soft glow. Rendered on the /line workbench for
 * tuning, then exported to a crisp PNG.
 */
@Component( {
  selector: 'app-line-passcode',
  templateUrl: './line-passcode.component.html',
  styleUrls: [ './line-passcode.component.scss' ],
} )
export class LinePasscodeComponent {

  /** Filled (entered digit) vs an empty slot. */
  @Input() on = false;

  /**
   * Everest (owner 2026-07-17): plain dots read too generic — the passcode is
   * a kawaii mountain instead. OFF = a faint, washed mountain (the icy OFF
   * tone); ON = the mountain "lit" by a dawn halo rising behind the summit,
   * body defined, snowcap bright.
   *
   * Sakura (owner 2026-07-24): each dot is a kawaii cherry blossom, and the
   * four are all a little different from one another — a different pink tint,
   * rotation, size and centre per digit (driven by `seed`). OFF = the blossom
   * as an empty pink outline; ON = the blossom filled, with a soft halo and a
   * warm golden stamen centre.
   */
  @Input() variant: 'dot' | 'mountain' | 'blossom' | 'tower' | 'star' | 'peak' | 'orb' | 'tree' | 'maple' | 'tepui' | 'tumbleweed' | 'sun' | 'palm' | 'gull' | 'eiffel' | 'parasol' | 'snowflake' | 'jellyfish' | 'shell' | 'lamp' | 'yacht' | 'basalt' | 'flower' | 'violin' | 'clavel' | 'volcano' | 'fuji' = 'dot';

  /** Fill colour for the ON state (a theme colour circle). */
  @Input() color = '#549EFF';

  /** Ring colour for the OFF state (the theme's muted foreground). */
  @Input() offColor = '#85A2B3';

  /** Which of the four passcode digits this dot is (0..3). Drives the sakura
   *  blossom's per-flower variation so the four never look identical. */
  @Input() seed = 0;

  /** Unique suffix so each dot's glow <filter> id can't collide. */
  readonly uid = 'pc-' + ( _pcUid++ );

  // ---- Istanbul GULL variant (owner 2026-08-14: "martılar") ----
  // The SAME bird Roraima's wallpaper flies, from the one shared module
  // (line-bird-glyph) and at the flock's own weight — drawn large enough to
  // fill the tile on its own. All four dots are that one drawing and differ
  // ONLY in colour, which is the rule Sakura set and Roraima's sun repeated; a
  // gull that changed pose per digit was rejected. ON = the gull filled in the
  // entered colour over a soft halo; OFF = the same gull flat in the one pale
  // tone every unentered dot shares (see GULL_PC_COLORS / GULL_PC_OFF).
  // ---- Paris EIFFEL variant (owner 2026-08-14: home and passcode are both the
  // tower) ----
  //
  // Paris is the family's first INVERTED passcode screen: the dots sit on a PALE
  // rose upper zone with the hot-pink keypad BELOW them, where every other theme
  // puts the colour on top. So this is also the first variant whose glyph has to
  // be DARK. The tower's own Eiffel Gold measures 1.79:1 on that zone and is
  // simply not there; the four ON tones are the palette's deep end instead, and
  // they read as the tower against the evening rather than lit up in it.
  //
  // All four are ONE drawing and differ only in colour, the rule Sakura set and
  // Roraima's sun repeated. The run walks from the softest to the deepest, so a
  // filling passcode reads as the sky going down.
  readonly eiffelSolid = EIFFEL_SOLID;
  readonly eiffelBase = EIFFEL_BASE;
  readonly eiffelDecks = EIFFEL_DECKS;
  readonly eiffelShade = EIFFEL_LEDGE_SHADOW;
  /** Measured on the #FFDADA upper zone: 2.84, 4.82, 6.89 and 9.10 to 1. */
  readonly eiffelOn = [ '#A9788A', '#994753', '#6A3C4D', '#532D33' ];
  /** ONE tone for every unentered tower — the frame never changes per digit.
   *  Rose Smoke at 1.55:1, present but plainly unlit beside the filled ones. */
  readonly eiffelOff = '#E3B9C2';

  // ---- Rio PLAJ ŞEMSİYESİ variant (owner 2026-08-14: "bari plaj şemsiyesi yap
  // iki renk sarı yeşil") ----
  //
  // Rio is the second theme here whose lock screen and Home tab carry different
  // glyphs. Cristo Redentor keeps the tab, where it appears once and reads as a
  // landmark; four of it across a passcode screen changes the register of the
  // whole thing, which is the same objection that killed Paris's fleur-de-lys
  // wallpaper after the drawing had come out well.
  //
  // Two colours, green and yellow, alternating six panels — and the green is on
  // the OUTSIDE, because the yellow measures 1.6:1 on this skin's white passcode
  // zone and a canopy edged in it would have no edge at all.
  readonly parasolPath = PARASOL_SOLID;
  readonly parasolA = PARASOL_PANELS_A;
  readonly parasolB = PARASOL_PANELS_B;
  readonly parasolPole = PARASOL_POLE;
  readonly parasolGreen = PARASOL_GREEN;
  readonly parasolYellow = PARASOL_YELLOW;
  readonly parasolOff = PARASOL_PC_OFF;

  /** Each dot leans by its own angle about the point where the pole enters the
   *  sand, at its own size — one authored table in the glyph module
   *  (PARASOL_POSES), because the size used to be derived from the lean and a
   *  derived size can only say "leans more, drawn smaller". */
  get parasolTransform(): string {
    const p = PARASOL_POSES[ this.seed % PARASOL_POSES.length ];
    return `translate(${ PARASOL_PIVOT[ 0 ] },${ PARASOL_PIVOT[ 1 ] }) rotate(${ p.a })`
      + ` scale(${ p.k }) translate(${ -PARASOL_PIVOT[ 0 ] },${ -PARASOL_PIVOT[ 1 ] })`;
  }

  // ---- Tallinn KAR TANESİ variant (owner 2026-08-14) ----
  //
  // The same flake the Home tab and the chat wallpaper draw. Its dots sit on the
  // skin's CYAN upper zone, so they are LIGHT — snow on a cyan block has nowhere
  // else to go — and the four differ by a breath of ice-blue rather than by
  // colour, which keeps them one object lighting up rather than four objects.
  readonly snowflake = SNOWFLAKE;
  readonly snowOff = SNOW_PC_OFF;

  /** Each flake leans by its own small angle about its centre — see
   *  SNOW_PC_TILTS for why "small" is the whole point on a six-fold shape. No
   *  scale rides along with it the way the parasol's does: the flake is centred
   *  in its box and a 16-degree turn cannot push it out. */
  get snowTransform(): string {
    return `rotate(${ SNOW_PC_TILTS[ this.seed % SNOW_PC_TILTS.length ] } 60 60)`;
  }

  readonly gullPath = GULL_PASSCODE;
  readonly gullOff = GULL_PC_OFF;

  /**
   * A small per-digit lean (owner 2026-08-14: "passcode'da martıların hepsi
   * farklı açı olsun birazcık sağa sola").
   *
   * This is a deliberate exception to the family's one-drawing rule, and it is
   * the colour change that earned it: when the four dots ran four different
   * colours, sameness of pose was what held the row together. Now every entered
   * gull is the same white, so identical poses read as one bird stamped four
   * times. The lean gives the row four birds instead.
   *
   * Kept SMALL and alternating. A gull rotated far stops being a gull — the same
   * limit the wallpaper's flock works under, where the tilt never leaves a few
   * degrees either side of level.
   */
  get gullTilt(): string {
    const lean = [ -7, 5, -4, 8 ][ this.seed % 4 ];
    return `rotate(${ lean } 60 68)`;
  }

  // ---- Ocean DENİZANASI variant ----
  //
  // The same jellyfish the Home tab and the chat wallpaper draw, and the first
  // passcode in this family to sit on a DARK upper zone since Roraima's. That is
  // the whole reason its four tones can be what they are: on a dark ground the
  // lit dots measure above 7:1 while the unentered one sits at 1.90, a gap of
  // nearly four to one. Every light-skinned theme here has had to fight for a
  // third of it.
  //
  // The four change HUE per digit rather than walking one hue from pale to deep
  // (Tallinn's flakes) — mint, lagoon, orchid, coral, the theme's own colour
  // circles taken up into the bioluminescence the theme is named for. A passcode
  // filling up here should read as four creatures lighting up in the dark, not as
  // one creature at four exposures, and hue is what says that at 30px.
  //
  // The ON dot draws the bell over deeper strands with a pale crest inside it,
  // exactly the three-tone stack the Home tab exports, so the lock screen and the
  // tab bar can never show two different animals.
  readonly jellyBell = JELLY_BELL;
  readonly jellyArms = JELLY_ARMS;
  readonly jellyTentacles = JELLY_TENTACLES;
  readonly jellyCrest = JELLY_CREST;
  readonly jellySolid = JELLYFISH;
  readonly jellyOff = JELLY_PC_OFF;

  /** A per-digit lean, alternating side and pivoted at the TOP of the bell so the
   *  strands swing and the crown stays put — a jellyfish hangs from its bell, and
   *  turning it about its centre reads as tumbling rather than as drifting.
   *
   *  Kept small. Past about 12 degrees the strands leave the box on one side and
   *  the animal stops being centred in its dot. */
  get jellyTransform(): string {
    return `rotate(${ [ -9, 6, -5, 11 ][ this.seed % 4 ] } 60 24)`;
  }
  /** The strands and the crest, derived from the entered colour rather than
   *  pinned — one colour change carries the whole animal (see line-menu-button,
   *  which derives the same two tones the same way). */
  get jellyDeep(): string { return mixHex( this.color, '#062024', 0.42 ); }
  get jellyCrestColor(): string { return mixHex( this.color, '#FFFFFF', 0.72 ); }

  // ---- Salda DENİZ KABUĞU variant ----
  //
  // The same scallop the Home tab and the chat wallpaper draw, on this skin's
  // PALE SAND upper zone — so the shells are DARK, the opposite of Ocean's lit
  // jellies, and the four walk from the shallows out to the deep.
  //
  // The fourth is the theme's driftwood brown rather than a fourth blue. Three
  // steps is all one hue has on a ground this pale before the next one stops
  // being turquoise at all, and a set that ends on a colour it never claimed to
  // be reads better than one that ends on ink.
  readonly shellBody = SHELL_BODY;
  readonly shellEars = SHELL_EARS;
  readonly shellRibs = SHELL_RIBS;
  readonly shellSolid = SHELL;
  readonly shellOff = SHELL_PC_OFF;
  /** Each shell leans by its own small angle about the HINGE — a shell lying on
   *  sand pivots where it rests, not about its middle. */
  get shellTransform(): string {
    return `rotate(${ SHELL_PC_TILTS[ this.seed % SHELL_PC_TILTS.length ] } 60 100)`;
  }
  /** The shell's ribs — the radial grooves, and the thing that makes a scallop a
   *  scallop rather than a fan.
   *
   *  A WARM shadow, never a cold one: an early pass mixed toward #0A2E36 and put
   *  dark teal ink inside a warm object, which is half of why the whole glyph once
   *  read as a turquoise fan.
   *
   *  And a DEEP one, 62% of the way to the shell's own brown. At 42% on a cream
   *  body the grooves measured 1.35:1 and the owner read the shell as flat; at 62%
   *  they are 1.92:1 — plainly grooves, and still nowhere near the dark brown that
   *  got this theme sent back in the first place. */
  get shellRibColor(): string { return mixHex( this.color, '#9A6E3E', 0.62 ); }

  // ---- Prague SOKAK LAMBASI variant ----
  //
  // The same gas street lamp the Home tab draws, on this skin's NIGHT upper zone.
  // It replaced a gothic spire, which the owner read as a religious emblem rather
  // than as a city — see the note in line-lamp-glyph.
  //
  // The four LIGHT UP: cold iron, the first blue of a mantle catching, lamplight,
  // then the full warm gold. A passcode filling up is the lamplighter going down
  // the street, which is a thing that still happens in this city every evening.
  readonly lampIron = LAMP_IRON;
  readonly lampGlass = LAMP_GLASS;
  readonly lampSolid = LAMP;
  readonly lampOff = LAMP_PC_OFF;
  /** A per-digit lean about the FOOT, and one of the smallest sets in the run: a
   *  lamp post is a plumb-line object (see LAMP_PC_TILTS). */
  get lampTransform(): string {
    return `rotate(${ LAMP_PC_TILTS[ this.seed % LAMP_PC_TILTS.length ] } 60 108)`;
  }
  get lampLit(): string { return mixHex( this.color, '#FFF6DC', 0.72 ); }

  // ---- Fuji MOUNTAIN variant ----
  //
  // Fuji itself, the same mountain the Home tab and the chat wallpaper draw. It
  // replaced a pagoda: that was picked to avoid a third triangle in a family that
  // already had Everest's peak and Etna's cone, and the owner's answer is that
  // Fuji is not a third anything.
  //
  // The four walk ACROSS the palette rather than down one hue — dawn slate,
  // wisteria, the icon's vermilion, then the deep umber of first light. This
  // theme's palette IS a dawn, and a dawn is a sequence of different colours
  // rather than one colour getting darker.
  readonly fujiMount = FUJI_MOUNT;
  readonly fujiSnow = FUJI_SNOW;
  readonly fujiOff = PAGODA_PC_OFF;
  /** A per-digit lean about the mountain's FOOT, and a small one: this is a
   *  specific mountain and a tilted Fuji is not Fuji. */
  get fujiTransform(): string {
    return `rotate(${ PAGODA_PC_TILTS[ this.seed % PAGODA_PC_TILTS.length ] } 60 96)`;
  }
  get fujiSnowColor(): string { return mixHex( this.color, '#FFFFFF', 0.86 ); }
  /** The cap on an UNENTERED dot: the upper zone's own blush, so the snow is the
   *  zone showing through the mountain rather than a mark laid over it. */
  readonly fujiSnowCut = '#FFE8E1';

  // ---- Etna YANARDAĞ variant ----
  //
  // The same volcano the Home tab draws, on this skin's NEAR-BLACK upper zone.
  //
  // The four HEAT UP, and this is the one ramp in the run that is literally a
  // physical process rather than a metaphor for one: cooled basalt, rock that is
  // still hot, moving lava, then the orange at the vent. A passcode filling up is
  // an eruption starting.
  readonly volcanoCone = VOLCANO_CONE;
  readonly volcanoShoulder = VOLCANO_SHOULDER;
  readonly volcanoFissure = VOLCANO_FISSURE;
  readonly volcanoRiver = VOLCANO_RIVER;
  readonly volcanoGlow = VOLCANO_GLOW;
  readonly volcanoPlume = VOLCANO_PLUME;
  readonly volcanoSolid = VOLCANO;
  readonly volcanoOff = VOLCANO_PC_OFF;
  /** A per-digit lean about the BASE, and the smallest in the run after Prague's:
   *  a mountain is a plumb-line object (see VOLCANO_PC_TILTS). */
  get volcanoTransform(): string {
    return `rotate(${ VOLCANO_PC_TILTS[ this.seed % VOLCANO_PC_TILTS.length ] } 60 106)`;
  }
  get volcanoRock(): string { return mixHex( this.color, '#100B0B', 0.62 ); }
  get volcanoVent(): string { return mixHex( this.color, '#FFE0A8', 0.68 ); }
  /**
   * The plume's ash.
   *
   * It is NOT the mountain's own colour. Painted in `color` it came back as a fat
   * dun BLOB over the crater, because this theme's ramp runs through a mid brown.
   *
   * It is a MEASURED ink rather than a pretty one, because the plume is the only
   * part of this glyph that has to survive on both faces of the iOS-26 glass tab:
   * #8B7B72 sits at relative luminance 0.22, which clears 3.8:1 on the light pill
   * and 4.9:1 on the dark glass. A paler ash looked better on the phone's dark
   * skin and disappeared completely on the light one. The theme's own colour is
   * mixed in at an eighth, which is enough to warm it and not enough to move it
   * out of that window.
   */
  get volcanoAsh(): string { return mixHex( '#8B7B72', this.color, 0.12 ); }

  // ---- Madrid ÇURROS variant ----
  //
  // THE CLAVEL, the same carnation the Home tab draws, on this skin's cream upper
  // zone — so the four are DARK, and all four are RED.
  //
  // The subject moved three times in one evening: churros con chocolate, a
  // flamenca, a bull, then this. The dancer is the one worth remembering — a human
  // figure is the hardest possible subject at this size, because arms and a neck
  // are the first features to vanish and the first to weld to what is beside them.
  // See line-clavel-glyph for why the carnation and not a rose.
  readonly clavelSolid = CLAVEL;
  readonly clavelHead = CLAVEL_HEAD;
  readonly clavelInner = CLAVEL_INNER;
  readonly clavelFoliage = CLAVEL_FOLIAGE;
  readonly clavelGreen = CLAVEL_GREEN;
  readonly clavelOff = CLAVEL_PC_OFF;
  /** A small per-digit lean, alternating side, about the foot of the stem. */
  get clavelTransform(): string {
    return `rotate(${ CLAVEL_PC_TILTS[ this.seed % CLAVEL_PC_TILTS.length ] } 60 112)`;
  }
  /** The inner ring, a shade deeper than the head so it reads as behind it. */
  get clavelShade(): string { return mixHex( this.color, '#3A0F0C', 0.34 ); }

  // ---- Vienna KEMAN variant ----
  //
  // The same violin the Home tab draws, on this skin's DEEP WINE upper zone. The
  // four warm up rather than change subject: aged wood, varnish catching the
  // light, the imperial gold, then the full house-lights cream. A passcode filling
  // up is the lights coming up on an instrument.
  readonly violinSolid = VIOLIN;
  readonly violinFholes = VIOLIN_FHOLES;
  readonly violinBridge = VIOLIN_BRIDGE;
  readonly violinStrings = VIOLIN_STRINGS;
  readonly violinOff = VIOLIN_PC_OFF;
  readonly violinSheen = VIOLIN_SHEEN;
  readonly violinShade = VIOLIN_SHADE;
  /** The varnish highlight, and its opposite. Light on one shoulder alone is a
   *  scratch; light one side and shade the other and the body is round. */
  get violinShine(): string { return mixHex( this.color, '#FFF3D6', 0.46 ); }
  get violinDark(): string { return mixHex( this.color, '#2A1008', 0.34 ); }
  /** A per-digit lean about the BOTTOM of the body, where a violin rests when it
   *  is stood on end (see VIOLIN_PC_TILTS). */
  get violinTransform(): string {
    return `rotate(${ VIOLIN_PC_TILTS[ this.seed % VIOLIN_PC_TILTS.length ] } 60 109)`;
  }
  get violinInside(): string { return mixHex( this.color, '#180A08', 0.62 ); }
  get violinString(): string { return mixHex( this.color, '#FFF6E2', 0.72 ); }

  // ---- Spring ÇAN ÇİÇEĞİ variant ----
  //
  // The same bellflower the Home tab draws, on this skin's PALE MEADOW upper zone.
  // It replaced a hummingbird the owner called careless — a bird in flight has no
  // stable silhouette and a hanging bell is one shape.
  //
  // Spring's ÇİÇEK — an upward-facing meadow flower, the same one the Home tab
  // draws. It replaced a bellflower, which nodded, and a nodding flower reads as
  // a sad one.
  readonly flowerSolid = FLOWER;
  readonly flowerFoliage = FLOWER_FOLIAGE;
  readonly flowerHead = FLOWER_HEAD;
  /** The foliage green, fixed for every digit — see FLOWER_GREEN. */
  readonly flowerGreen = FLOWER_GREEN;
  readonly flowerShade = FLOWER_SHADE;
  readonly flowerOff = FLOWER_PC_OFF;
  /** The two OUTER petals, a shade deeper than the centre one. A tulip is not one
   *  flat colour — the side petals turn away from the light — and this is the whole
   *  of the modelling that stops the cup reading as a balloon. */
  get flowerShadeTone(): string { return mixHex( this.color, '#2A1038', 0.30 ); }
  /** A per-digit lean about the stem's FOOT. Wider than the bellflower's, which
   *  had to stay tight because a nodding bell turned too far reads as a broken
   *  stem — an upward-facing head is the same flower at any angle. */
  get flowerTransform(): string {
    return `rotate(${ FLOWER_PC_TILTS[ this.seed % FLOWER_PC_TILTS.length ] } 60 112)`;
  }

  readonly basaltFaces = BASALT_FACES;
  readonly basaltFacesAlt = BASALT_FACES_ALT;
  readonly basaltTops = BASALT_TOPS;
  readonly basaltSolid = BASALT;
  readonly basaltOff = BASALT_PC_OFF;
  /** The shaded half of the columns, and the pale broken tops. Same pair the tab
   *  glyph uses — two tones are what separate faces that physically touch. */
  get basaltAltTone(): string { return mixHex( this.color, '#0E1216', 0.42 ); }
  get basaltTopTone(): string { return mixHex( this.color, '#FFFFFF', 0.46 ); }
  /** NO per-digit tilt. The puffin this replaced leaned; a basalt column does not
   *  — a leaning column is a fallen one, which is the same note Prague's lamp
   *  carries and for the same reason. */

  // ---- Sydney YELKENLİ variant ----
  //
  // The same boat the Home tab draws, on this skin's PALE HARBOUR upper zone, so
  // the dots are DARK. It replaced the Opera House, which was drawn twice before
  // the subject rather than the drawing turned out to be the fault.
  //
  // The four go DEEPER the way water does away from a beach — shallow over sand,
  // the harbour proper, the channel — and then one warm step, the bridge's own
  // ochre, which is the only thing in this theme that is not blue.
  readonly yachtHull = YACHT_HULL;
  readonly yachtMast = YACHT_MAST;
  readonly yachtMain = YACHT_MAIN;
  readonly yachtJib = YACHT_JIB;
  readonly yachtSolid = YACHT;
  /** The hull and the rig, a step DEEPER than the sails. Water-line down is the
   *  wet half of a boat and it is always the darkest part of one. */
  get yachtHullTone(): string { return mixHex( this.color, '#06384A', 0.46 ); }
  /** The jib, a step LIGHTER. The two sails have to separate somehow, and tone is
   *  the only way a flat fill can do it — the mast between them is two units wide
   *  and gone by 30px. */
  /** The RIGHT-hand sail, YELLOW, picked by digit rather than derived from the
   *  mainsail's blue (owner 2026-08-15). It was `mixHex(color, '#EAF6FA', 0.34)`,
   *  i.e. the same blue a step paler, which made the boat one hue at two
   *  exposures. Read from YACHT_PC_JIBS so the preview and the exported PNG take
   *  the same pairing. */
  get yachtJibTone(): string {
    return YACHT_PC_JIBS[ ( ( this.seed % YACHT_PC_JIBS.length ) + YACHT_PC_JIBS.length )
      % YACHT_PC_JIBS.length ];
  }
  readonly yachtOff = YACHT_PC_OFF;
  /** A per-digit lean about the HULL. A yacht under sail heels; a boat drawn dead
   *  upright is a boat in a marina (see YACHT_PC_TILTS). */
  get yachtTransform(): string {
    return `rotate(${ YACHT_PC_TILTS[ this.seed % YACHT_PC_TILTS.length ] } 60 96)`;
  }

  // ---- Miami PALM variant (owner 2026-08-13) ----
  // The foreground of the theme's own icon, and the same tree the Home tab
  // draws. Unlike Roraima's sun, the four dots DO change colour per digit: they
  // take the theme's colour circles, so the lock screen lights up pink, coral,
  // aqua and gold in turn, which is the neon this theme is named for.
  readonly palmCrown = MIAMI_PALM_CROWN;
  readonly palmTrunk = MIAMI_PALM_TRUNK;
  /* The unentered palm used to be a FILLED silhouette in the faded circle-1 the
   * idle tab glyphs use. It is hollow now, so that tone lives only in
   * line-tab-colors and this component no longer carries a copy of it. */
  /**
   * The unentered palm is the whole tree FILLED in the trunk ink (owner
   * 2026-08-13: "normal içi dolu outline olmayan hali olsun").
   *
   * A hollow light-pink outline was tried in between and dropped: the crown is
   * ~30 overlapping leaflets, so outlining it draws every blade's edge twice and
   * the canopy closes into a white blob. Weight did not save it — at 2.8 it was
   * solid, and the 1.6 that stayed open would have been a third of a pixel once
   * LINE scales a 120px dot down to the ~30px it actually shows.
   */
  readonly palmOff = MIAMI_PALM_INK;
  /** Entered: trunk stays the pink, canopy lights up in the theme colour. */
  readonly palmTrunkInk = MIAMI_PALM_INK;
  /* A circle-1 KEYLINE around the lit palm was tried here and removed the same
   * day (owner: "ON'lardaki borderları kaldır"). It thickened a crown that was
   * already dense, and it was what finally pushed the artwork past the viewBox
   * edge. The palm is bolder instead — see MIAMI_PALM_FIT. */
  /** A per-digit lean, so four palms are not one asset repeated. */
  /** A per-digit lean, alternating left and right (owner 2026-08-13: "kimisi
   *  sola kimisi sağa yatık", and "çok değil de biraz"), pivoted at the trunk's
   *  foot so the crown swings and the base stays planted. */
  private readonly plRot = [ -9, 7, -6, 10 ];
  get plTransform(): string {
    return `${ MIAMI_PALM_FIT } rotate(${ this.plRot[ this.seed % 4 ] } 60 96)`;
  }

  // ---- Roraima SETTING SUN variant (owner 2026-08-13, the shipped one) ----
  // "batan güneş daha iyi olur", replacing the tumbleweed below. All four dots
  // are the SAME low sun and differ only in colour (owner: "hepsi 3.sü olsun
  // sadece renkleri farklı olsun"), which is Sakura's rule — one design, small
  // variation inside it. The sunset lives in the palette, not the geometry.
  readonly sunR = SUN_R;
  readonly sunHorizonY = SUN_HORIZON;
  readonly sunHorizonD = SUN_HORIZON_D;
  readonly sunOff = SUN_OFF;
  readonly sunCy = SUN_CY;
  get sunLight(): string { return SUN_COLORS[ this.seed % 4 ].light; }
  get sunDeep(): string { return SUN_COLORS[ this.seed % 4 ].deep; }
  readonly sunRaysD = sunRays( SUN_CY, SUN_RAY_LEN );
  /** Two thin clouds crossing the disc — the same bank shape the wallpaper and
   *  the menu strip drift, so all three carry one weather. Fixed, not per digit:
   *  colour is the only thing that varies across the four. */
  readonly sunWisp = TEPUI_WISP;
  readonly sunWisp1 = `translate(55,${ SUN_CY + 6 }) scale(1.5,0.7)`;
  readonly sunWisp2 = `translate(71,${ SUN_CY - 8 }) scale(1,0.6)`;

  // ---- Roraima tumbleweed variant (owner 2026-08-13, REJECTED, kept switchable
  // the way 'mountain' and 'tower' are) ----
  // "çölde uçuşan saman yuvarlak topağı, roraima sarısı ile". This REPLACES the
  // tepui on the dots; the wall keeps the Home tab, the menu strip and the
  // wallpaper. Sakura has the same split between its blossom and its petals.
  //
  // ONE colour for all four, the theme's Dry Grass — the owner asked for the
  // yellow, so the dots no longer cycle the four colour circles the way the
  // tepui pass did. The four differ by carrying four DIFFERENT tangles plus a
  // per-digit roll, which is what a tumbleweed can do and a table mountain
  // could not.
  readonly tumbleStraw = TUMBLE_STRAW;
  readonly tumbleBody = TUMBLE_BODY;
  readonly tumbleOff = TUMBLE_OFF;
  readonly tumbleR = TUMBLE_R;
  get twWrap(): string { return TUMBLEWEED_WRAP[ this.seed % 4 ]; }
  get twHook(): string { return TUMBLEWEED_HOOK[ this.seed % 4 ]; }
  /** A per-digit roll, so the four read as one weed caught at four moments. */
  private readonly twRot = [ 0, 34, -21, 57 ];
  get twTransform(): string { return `rotate(${ this.twRot[ this.seed % 4 ] } 60 60)`; }

  // ---- Roraima tepui variant (2026-08-13) ----
  // The wall from the theme's OWN ICON, traced (see line-tepui-glyph), with its
  // detached pillar — the one detail that separates Roraima from any mesa.
  //
  // The four dots do NOT vary by tilt, the way every other variant here does: a
  // tepui is defined by a dead-level rim, and rotating it is the single thing
  // that would stop it reading as a table mountain. They vary by the LIGHT
  // instead. Each digit takes its own colour circle (coral, dry grass, pink
  // quartz, orchid mist) for the sun and the lit rim, and the sun sits a little
  // higher each time, so entering a passcode plays the theme's own subject: a
  // sunset over Roraima, four moments of it.
  readonly tpBody = TEPUI_BODY;
  readonly tpSpire = TEPUI_SPIRE;
  readonly tpCrown = TEPUI_CROWN;
  readonly tpRunnels = TEPUI_RUNNELS;
  readonly tpDeep = TEPUI_DEEP;
  readonly tpMid = TEPUI_MID;
  readonly tpSeam = TEPUI_SEAM;
  readonly tpSun = TEPUI_SUN;
  /** ONE outline tone for every unentered wall — the frame never changes per
   *  digit (the rule Aurora's orbs settled). */
  readonly tpOff = TEPUI_OFF;
  /** The sun SINKS as the digits go in — clear of the rim on the first, half
   *  swallowed by it on the fourth. It has to go down, not up: the four colour
   *  circles run coral, gold, pink, orchid, which is a sunset in order, and a
   *  disc climbing through those colours would read as a moon rising instead. */
  private readonly tpRise = [ -5, -1, 3, 7 ];
  get tpSunCy(): number { return this.tpSun.cy + this.tpRise[ this.seed % 4 ]; }
  /** The lit rim. The face's gradient already tops out ON this digit's colour,
   *  so painting the crown the same colour makes it disappear — it has to be a
   *  step lighter to read as the edge catching the light. */
  get tpCrownFill(): string { return mixHex( this.color, '#FFFFFF', 0.42 ); }

  // ---- Autumn maple variant (2026-08-13) ----
  // The leaf from the theme's OWN ICON, traced (see line-maple-glyph) — the
  // object the owner asked both the passcode and the Home tab to carry. ON = the
  // leaf lit, shading ember at the base into amber at the crown; OFF = the same
  // blade as an empty outline in one muted tone.
  readonly mapleBlade = MAPLE_BLADE;
  readonly mapleDeep = MAPLE_DEEP;
  readonly mapleMid = MAPLE_MID;
  readonly mapleLight = MAPLE_LIGHT;
  readonly mapleGold = MAPLE_GOLD;
  /** ONE outline tone for every unentered leaf — the frame never changes per
   *  digit (the rule Aurora's orbs settled). */
  readonly mapleOff = '#C2A48E';
  /** A per-digit tilt, so four leaves are not one asset repeated. */
  private readonly mpRot = [ -8, 5, -3, 9 ];
  get mpTransform(): string {
    return `rotate(${ this.mpRot[ this.seed % 4 ] } 60 62)`;
  }

  // ---- Sequoia leaf variant (2026-08-13) ----
  // A SINGLE leaf per digit, not a whole tree (owner) — and a classic broad
  // blade rather than the needled frond tried first, which read as too thin at
  // dot size. The tree stays on the Home tab, where foliage would turn to mush
  // at 22px; this is the same blossom-and-petal relationship Sakura has.
  readonly leafBlade = LEAF_BLADE;
  readonly leafVeins = LEAF_VEINS;
  readonly treeDark = TREE_DARK;
  readonly treeMid = TREE_MID;
  readonly treeLight = TREE_LIGHT;
  /** The one outline tone every unentered frond uses. */
  readonly treeOff = '#9DAF92';
  /** A per-digit tilt, so four fronds are not one asset repeated. */
  private readonly trRot = [ -6, 4, -2, 7 ];
  get trTransform(): string {
    return `rotate(${ this.trRot[ this.seed % 4 ] } 60 60)`;
  }

  // ---- Aurora night-orb variant (owner 2026-08-12) ----
  // Two earlier attempts were thrown out: a cut gem (nothing to do with an
  // aurora) and a bare curl of light (read as a leaf — an aurora shape floating
  // free has no context to be an aurora IN).
  //
  // So the dot keeps the round passcode indicator LINE users already expect, and
  // the creativity goes inside it: each entered digit is a porthole onto the
  // theme's own night. The circle also solves what made the loose curl ugly,
  // since each band's ends are cut off by the rim instead of tapering to points.
  //
  // Every orb mixes ALL THREE aurora colours rather than being monochrome, and
  // the four differ by which colour leads, how the bands are stacked and how the
  // sky is tilted — four skies, one place. OFF is the same orb with the light
  // switched off: dark glass, stars, dim rim, no aurora.
  /** Bigger than the plain dot it replaces (owner 2026-08-12): at r=38 the orb
   *  fills about two thirds of the 120 tile, which is what it needs to read as a
   *  sky rather than a bead. */
  readonly orbR = ORB_R;
  get orbBands(): { d: string; fill: string; o: number }[] {
    return ORB_SETS[ this.seed % 4 ].map( b => ( {
      d: ORB_BANDS[ b.band ], fill: ORB_COLORS[ b.color ], o: b.o,
    } ) );
  }
  /** Stars: a different arrangement per digit, and not always the same count
   *  (owner 2026-08-12), so four lit orbs never look like one asset repeated. */
  get orbStars(): { cx: number; cy: number; r: number }[] {
    return ORB_STARS[ this.seed % 4 ];
  }
  /** ONE rim colour family for all four, never per digit (owner 2026-08-12:
   *  "border renkleri aynı olacak"). Sakura's rule applies here too — the four
   *  dots share a single design and vary only inside it, so the frame must not
   *  change per digit. It does change per STATE: the lit ring runs the full
   *  aurora sweep — blue, green, pink, purple — while the unlit one is a muted
   *  deep navy (owner 2026-08-12). The WEIGHT is identical on all eight. */
  readonly orbRimStops = ORB_RIM_STOPS;
  /** The halo behind a lit orb: the sweep's blue end, so it stays one soft tone. */
  readonly orbGlow = '#549EFF';
  readonly orbRimOff = ORB_RIM_OFF;
  readonly orbStroke = 5;
  /** A per-digit tilt so the four skies are not copy-paste identical. */
  private readonly orbRot = [ -10, 8, -5, 14 ];
  get orbTransform(): string {
    return `rotate(${ this.orbRot[ this.seed % 4 ] } 60 60)`;
  }

  // ---- Sakura blossom variant ----
  // All four passcode blossoms share ONE design — the wide heart-lobed
  // five-petal flower with a golden stamen centre (owner 2026-07-24: "4.
  // çiçeğin tasarımı güzel, onu kullanalım") — and differ only by a small
  // colour shift per digit (a gentle rotation keeps the four from looking
  // copy-pasted), the way the first pass varied colour alone.

  /** The chosen blossom: wide heart-lobed petals with a deep valley. */
  readonly skPetal = 'M0,-12 C-13,-17 -14,-41 -6,-46 Q-2.5,-42 0,-37 Q2.5,-42 6,-46 C14,-41 13,-17 0,-12 Z';
  /** Five cherry-pink tints (small changes only), one per digit. */
  private readonly skPinks = [ '#E79BBB', '#EDA9C6', '#E28FB2', '#F0B4CE' ];
  /** A gentle per-digit rotation so the identical flowers still feel placed. */
  private readonly skRot = [ 0, 10, -8, 5 ];
  /** Golden stamen centre radius. */
  readonly skCore = 8;

  /** ON-blossom petal gradients — VARIED SAKURA PINKS (owner 2026-07-29): each
   *  dot is a different cherry-pink tone (deep centre → light tip), all within
   *  the pink family (a sakura is pink, never blue/green), with a warm golden
   *  stamen centre. Picked per digit by seed. */
  // Very light / airy pinks fading toward white (owner 2026-07-29: go light,
  // toward white, not dark). Each dot a slightly different pale sakura pink.
  private readonly skPinkPairs: { deep: string; light: string }[] = [
    { deep: '#F7BFD6', light: '#FFF6FB' }, // pale petal → near white
    { deep: '#FAC9DC', light: '#FFFFFF' }, // soft blush → white
    { deep: '#F5B4CF', light: '#FFF1F8' }, // light rose → near white
    { deep: '#F9C2D8', light: '#FFF8FC' }, // powder pink → near white
  ];
  get skDeep(): string { return this.skPinkPairs[ this.seed % this.skPinkPairs.length ].deep; }
  get skLight(): string { return this.skPinkPairs[ this.seed % this.skPinkPairs.length ].light; }

  /** This blossom's petal colour (per digit, a small variation). */
  get skColor(): string { return this.skPinks[ this.seed % 4 ]; }
  /** The five petal rotations, nudged per digit so no two align exactly. */
  get skTransforms(): string[] {
    const rot = this.skRot[ this.seed % 4 ];
    return [ 0, 1, 2, 3, 4 ].map( k => `rotate(${ +( k * 72 + rot ).toFixed( 1 ) })` );
  }
  /** Tiny golden stamen dots ringing the centre (part of the chosen design). */
  get skStamenTransforms(): string[] {
    return this.skTransforms.map( t => `${ t } translate(0,-16)` );
  }
  /** Centre-anchored group transform. */
  get skGroupTransform(): string { return 'translate(60,60) scale(1.18)'; }

  // ---- Tokyo Tower variant (owner 2026-08-05) ----
  // A DETAILED steel-LATTICE Tokyo Tower in ONE colour — the theme's colour
  // circle 2, passed in via `color`. A criss-cross truss mesh (wire-by-wire)
  // fills the silhouette, with two observation decks. OFF = a faint empty tower;
  // ON = the full lit lattice with a soft halo.

  /** The real Tokyo Tower colour: aviation "international orange". */
  readonly twColor = '#E8562F';

  /** Tokyo Tower silhouette — big but slim, with OPEN splayed legs whose inner
   *  edges are CONCAVE (the real tower's arch) and a small flat top to the arch;
   *  the base sits above the box bottom so there is space beneath the tower. */
  readonly twTower =
    'M60 10 L62.5 26 L68 38 L65 50 L69 60 Q71 80 90 102 L76 102 Q73 90 64 86 L56 86 Q47 90 44 102 L30 102 Q49 80 51 60 L55 50 L52 38 L57.5 26 Z';
  /** The two observation decks (main = shorter + higher, special above it), a
   *  touch wider than the body; the neck between them stays visible (not pinched). */
  readonly twDecks = [ { x: 46, y: 57, w: 28, h: 6 }, { x: 48, y: 35, w: 24, h: 5 } ];

  /** The lattice truss: criss-cross diagonals both ways + horizontal chords,
   *  clipped to the silhouette in the template so it reads as steel mesh. */
  get twMesh(): string[] {
    const lines: string[] = [];
    const run = 28;
    for ( let k = -70; k <= 70; k += 14 ) {
      const bx = 60 + k;
      lines.push( `M${ bx } 111 L${ bx + run } 8` );               // "/" brace
      lines.push( `M${ 120 - bx } 111 L${ 120 - bx - run } 8` );   // "\" = its mirror
    }
    for ( let y = 20; y <= 108; y += 12 ) {
      lines.push( `M12 ${ y } L108 ${ y }` );                      // horizontal chords
    }
    return lines;
  }

  /** A LIGHT tint of the tower's own colour, for the lattice + decks (owner:
   *  the mesh is a lighter shade of the tower colour, not white). */
  get twLight(): string {
    const h = ( this.color || '#E8562F' ).replace( '#', '' );
    const full = h.length === 3 ? h.split( '' ).map( c => c + c ).join( '' ) : h;
    const f = 0.58;
    const c = [ 0, 2, 4 ].map( i => {
      const v = parseInt( full.substr( i, 2 ), 16 ) || 0;
      return Math.round( v + ( 255 - v ) * f );
    } );
    return '#' + c.map( v => v.toString( 16 ).padStart( 2, '0' ) ).join( '' );
  }

  // ---- Tokyo Star variant (owner 2026-08-05: the tower is retired; each dot
  // is a soft 4-point twinkle, the SAME sparkle that drifts in the Tokyo chat
  // wallpaper). OFF = a faint outline star; ON = a filled star with a soft halo.

  /** A per-digit tilt so the twinkles don't all sit at the same angle. */
  private readonly starRot = [ 12, -17, 24, -8 ];
  get starTransform(): string { return `rotate(${ this.starRot[ this.seed % 4 ] } 60 60)`; }

  /** Ray count per digit — all 4-point twinkles (owner 2026-08-05). */
  private readonly starPoints = [ 4, 4, 4, 4 ];
  /** The twinkle outline for this digit: a rounded N-point sparkle (deep concave
   *  valleys pinched near the centre), centred in the 120 box. */
  get starPath(): string { return this.sparkle( this.starPoints[ this.seed % 4 ] ); }

  // ---- Everest Peak variant (the restart, 2026-08-06) ----
  // Everest's hero motif is its own summit, the way Sakura's is the blossom, and
  // the passcode dot is the SAME glyph the Home tab button draws (shared paths in
  // line-peak-glyph.ts). One peak, not a range (owner 2026-08-12).
  //
  // ON = a BLUE mountain under white snow with the sun coming up behind it
  // (owner 2026-08-12): the theme is "Everest at dawn", so the warmth belongs in
  // the sky, not on the rock. OFF = the same silhouette as an empty outline in
  // the tab bar's icy OFF tone, so filled reads "entered" and outlined reads
  // "remaining", like the Sakura blossoms.

  readonly pkBody = PEAK_BODY;
  readonly pkCap = PEAK_CAP;
  readonly pkCapSmall = PEAK_CAP_SMALL;
  /** A per-digit tilt so the four summits are not copy-paste identical. */
  private readonly pkRot = [ 0, -4, 3, -2 ];
  /** Scaled up about the summit's own centre so it fills the dot the way the
   *  Sakura blossom does, plus the per-digit tilt. */
  get pkTransform(): string {
    return `rotate(${ this.pkRot[ this.seed % 4 ] } 60 66) translate(60,66) scale(1.16) translate(-60,-66)`;
  }

  private sparkle( n: number, R = 42, rInner = 7, cx = 60, cy = 60 ): string {
    const pt = ( deg: number, r: number ): [ number, number ] => {
      const a = ( -90 + deg ) * Math.PI / 180;
      return [ +( cx + r * Math.cos( a ) ).toFixed( 1 ), +( cy + r * Math.sin( a ) ).toFixed( 1 ) ];
    };
    const outer: [ number, number ][] = [];
    for ( let i = 0; i < n; i++ ) { outer.push( pt( i * 360 / n, R ) ); }
    let d = `M${ outer[ 0 ][ 0 ] },${ outer[ 0 ][ 1 ] }`;
    for ( let i = 0; i < n; i++ ) {
      const q = outer[ ( i + 1 ) % n ];
      const c = pt( ( i + 0.5 ) * 360 / n, rInner );
      d += ` Q${ c[ 0 ] },${ c[ 1 ] } ${ q[ 0 ] },${ q[ 1 ] }`;
    }
    return d + ' Z';
  }
}
